import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  LinearProgress,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  BugReport,
  LocalFlorist,
  Straighten,
  Lightbulb,
  ThermostatAuto,
  Download,
  Share
} from '@mui/icons-material';

const ImageAnalysisResults = ({ analysis, imageUrl, onExportPDF, onShare }) => {
  const [expandedPanel, setExpandedPanel] = useState('health');

  // Logging pour debug
  React.useEffect(() => {
    if (analysis) {
      console.log('📊 ImageAnalysisResults - Données reçues:', {
        healthScore: analysis.healthScore,
        growthStage: analysis.growthStage,
        diseasesCount: analysis.diseasesDetected?.length || 0,
        pestsCount: analysis.pestsDetected?.length || 0,
        anomaliesCount: analysis.growthAnomalies?.length || 0,
        fruitsDetected: analysis.fruitMeasurements?.fruitsDetected || 0,
        recommendationsCount: analysis.recommendations?.length || 0,
        aiModel: analysis.aiModel,
        imageUrl: imageUrl
      });
    }
  }, [analysis, imageUrl]);

  if (!analysis) {
    return (
      <Alert severity="info">
        Aucune analyse disponible. Téléchargez une image pour commencer.
      </Alert>
    );
  }

  // Vérifier si toutes les sections sont vides
  const hasAnyData =
    analysis.healthScore !== undefined ||
    (analysis.diseasesDetected && analysis.diseasesDetected.length > 0) ||
    (analysis.pestsDetected && analysis.pestsDetected.length > 0) ||
    (analysis.growthAnomalies && analysis.growthAnomalies.length > 0) ||
    (analysis.fruitMeasurements && analysis.fruitMeasurements.fruitsDetected > 0) ||
    (analysis.recommendations && analysis.recommendations.length > 0);

  if (!hasAnyData) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>Analyse incomplète</Typography>
        <Typography variant="body2">
          L'analyse a été effectuée mais aucune donnée n'a été retournée par le modèle IA ({analysis.aiModel || 'inconnu'}).
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Cela peut indiquer:
        </Typography>
        <ul>
          <li>Une image de mauvaise qualité ou floue</li>
          <li>Une image ne contenant pas de plant de framboisier</li>
          <li>Un problème temporaire avec le service d'IA</li>
        </ul>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Essayez de télécharger une nouvelle image claire d'un plant de framboisier.
        </Typography>
      </Alert>
    );
  }

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  /**
   * Get severity color
   */
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'faible':
      case 'low':
        return 'success';
      case 'moyenne':
      case 'medium':
        return 'warning';
      case 'élevée':
      case 'haute':
      case 'high':
        return 'error';
      default:
        return 'default';
    }
  };

  /**
   * Get health score color
   */
  const getHealthScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  /**
   * Render health status section
   */
  const renderHealthStatus = () => {
    const healthScore = analysis.healthScore !== undefined ? analysis.healthScore : null;
    const growthStage = analysis.growthStage || 'Indéterminé';
    const overallStatus = analysis.overallStatus || { condition: 'indéterminé', notes: 'Données non disponibles' };

    // Si healthScore est null/undefined, afficher un message
    if (healthScore === null) {
      return (
        <Accordion
          expanded={expandedPanel === 'health'}
          onChange={handleAccordionChange('health')}
          elevation={2}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <LocalFlorist color="primary" />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                État de santé général
              </Typography>
              <Chip label="Données manquantes" color="warning" size="small" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info">
              Le score de santé n'a pas pu être déterminé pour cette analyse.
            </Alert>
          </AccordionDetails>
        </Accordion>
      );
    }

    return (
      <Accordion
        expanded={expandedPanel === 'health'}
        onChange={handleAccordionChange('health')}
        elevation={2}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <LocalFlorist color="primary" />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              État de santé général
            </Typography>
            <Chip
              label={`Score: ${healthScore}/100`}
              color={getHealthScoreColor(healthScore)}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Score de santé
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LinearProgress
                variant="determinate"
                value={healthScore}
                sx={{
                  flexGrow: 1,
                  height: 10,
                  borderRadius: 1,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: healthScore >= 80 ? 'success.main' : healthScore >= 60 ? 'warning.main' : 'error.main'
                  }
                }}
              />
              <Typography variant="h6" fontWeight="bold">
                {healthScore}%
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Stade de croissance
                  </Typography>
                  <Typography variant="h6">{growthStage}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    État général
                  </Typography>
                  <Typography variant="h6">
                    {overallStatus.condition || 'Normal'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {overallStatus.notes && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {overallStatus.notes}
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  /**
   * Render diseases section
   */
  const renderDiseases = () => {
    const diseases = analysis.diseasesDetected || [];
    const hasDiseases = diseases.length > 0;

    return (
      <Accordion
        expanded={expandedPanel === 'diseases'}
        onChange={handleAccordionChange('diseases')}
        elevation={2}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Warning color={hasDiseases ? 'warning' : 'action'} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Maladies détectées
            </Typography>
            <Badge badgeContent={diseases.length} color={hasDiseases ? 'error' : 'default'}>
              <Chip
                label={hasDiseases ? 'Problèmes détectés' : 'Aucune maladie'}
                color={hasDiseases ? 'warning' : 'success'}
                size="small"
              />
            </Badge>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!hasDiseases ? (
            <Alert severity="success">
              Aucune maladie phytosanitaire détectée. Vos plants semblent sains !
            </Alert>
          ) : (
            <List>
              {diseases.map((disease, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <ErrorIcon color={getSeverityColor(disease.severity)} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {disease.name}
                          </Typography>
                          <Chip
                            label={`Sévérité: ${disease.severity}`}
                            size="small"
                            color={getSeverityColor(disease.severity)}
                          />
                          <Chip
                            label={`Confiance: ${Math.round(disease.confidence * 100)}%`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" paragraph>
                            {disease.description}
                          </Typography>
                          {disease.location && (
                            <Typography variant="caption" color="text.secondary">
                              Localisation: {disease.location}
                            </Typography>
                          )}
                          {disease.symptoms && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" fontWeight="bold">
                                Symptômes:
                              </Typography>
                              <Typography variant="caption" display="block">
                                {disease.symptoms}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < diseases.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  /**
   * Render pests section
   */
  const renderPests = () => {
    const pests = analysis.pestsDetected || [];
    const hasPests = pests.length > 0;

    return (
      <Accordion
        expanded={expandedPanel === 'pests'}
        onChange={handleAccordionChange('pests')}
        elevation={2}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <BugReport color={hasPests ? 'error' : 'action'} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Nuisibles & Ravageurs
            </Typography>
            <Badge badgeContent={pests.length} color={hasPests ? 'error' : 'default'}>
              <Chip
                label={hasPests ? 'Infestation détectée' : 'Aucun nuisible'}
                color={hasPests ? 'error' : 'success'}
                size="small"
              />
            </Badge>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!hasPests ? (
            <Alert severity="success">
              Aucun nuisible ou ravageur détecté sur cette image.
            </Alert>
          ) : (
            <List>
              {pests.map((pest, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <BugReport color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {pest.name}
                          </Typography>
                          <Chip
                            label={`Niveau: ${pest.infestationLevel || pest.severity}`}
                            size="small"
                            color={getSeverityColor(pest.infestationLevel || pest.severity)}
                          />
                          <Chip
                            label={`Confiance: ${Math.round(pest.confidence * 100)}%`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" paragraph>
                            {pest.description}
                          </Typography>
                          {pest.developmentStage && (
                            <Typography variant="caption" display="block" gutterBottom>
                              <strong>Stade:</strong> {pest.developmentStage}
                            </Typography>
                          )}
                          {pest.location && (
                            <Typography variant="caption" color="text.secondary">
                              Localisation: {pest.location}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < pests.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  /**
   * Render growth anomalies section
   */
  const renderGrowthAnomalies = () => {
    const anomalies = analysis.growthAnomalies || [];
    const hasAnomalies = anomalies.length > 0;

    return (
      <Accordion
        expanded={expandedPanel === 'growth'}
        onChange={handleAccordionChange('growth')}
        elevation={2}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <LocalFlorist color={hasAnomalies ? 'warning' : 'action'} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Anomalies de croissance
            </Typography>
            <Chip
              label={hasAnomalies ? `${anomalies.length} anomalie(s)` : 'Croissance normale'}
              color={hasAnomalies ? 'warning' : 'success'}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!hasAnomalies ? (
            <Alert severity="success">
              Aucune anomalie de croissance détectée. Développement normal des plants.
            </Alert>
          ) : (
            <List>
              {anomalies.map((anomaly, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <Warning color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold">
                          {anomaly.type}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" paragraph>
                            {anomaly.description}
                          </Typography>
                          {anomaly.possibleCause && (
                            <Typography variant="caption" display="block">
                              <strong>Cause possible:</strong> {anomaly.possibleCause}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < anomalies.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  /**
   * Render fruit measurements section
   */
  const renderFruitMeasurements = () => {
    const fruits = analysis.fruitMeasurements || {};
    const hasMeasurements = Object.keys(fruits).length > 0;

    return (
      <Accordion
        expanded={expandedPanel === 'fruits'}
        onChange={handleAccordionChange('fruits')}
        elevation={2}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Straighten color="primary" />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Mesures des fruits
            </Typography>
            {hasMeasurements && fruits.count > 0 && (
              <Chip label={`${fruits.count} fruit(s)`} color="primary" size="small" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!hasMeasurements || !fruits.count ? (
            <Alert severity="info">
              Aucun fruit visible sur cette image ou mesures non disponibles.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Nombre de fruits
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {fruits.count}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {fruits.averageSize && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Taille moyenne
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {fruits.averageSize} mm
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {fruits.sizeRange && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Plage de taille
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" fontSize="1.2rem">
                        {fruits.sizeRange.min}-{fruits.sizeRange.max} mm
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {fruits.estimatedYield && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Rendement estimé
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" fontSize="1.2rem">
                        {fruits.estimatedYield}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  /**
   * Render recommendations section
   */
  const renderRecommendations = () => {
    const recommendations = analysis.recommendations || [];
    const hasRecommendations = recommendations.length > 0;

    return (
      <Accordion
        expanded={expandedPanel === 'recommendations'}
        onChange={handleAccordionChange('recommendations')}
        elevation={2}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Lightbulb color="secondary" />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Recommandations
            </Typography>
            <Chip
              label={`${recommendations.length} conseil(s)`}
              color="secondary"
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!hasRecommendations ? (
            <Alert severity="info">
              Aucune recommandation spécifique. Continuez les bonnes pratiques actuelles.
            </Alert>
          ) : (
            <List>
              {recommendations.map((rec, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <CheckCircle
                        color={
                          rec.priority === 'haute' ? 'error' :
                          rec.priority === 'moyenne' ? 'warning' : 'success'
                        }
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {rec.action}
                          </Typography>
                          <Chip
                            label={`Priorité: ${rec.priority}`}
                            size="small"
                            color={
                              rec.priority === 'haute' ? 'error' :
                              rec.priority === 'moyenne' ? 'warning' : 'success'
                            }
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" paragraph>
                            {rec.reason || rec.description}
                          </Typography>
                          {rec.timing && (
                            <Typography variant="caption" display="block" gutterBottom>
                              <strong>Timing:</strong> {rec.timing}
                            </Typography>
                          )}
                          {rec.products && rec.products.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" fontWeight="bold">
                                Produits suggérés:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {rec.products.map((product, idx) => (
                                  <Chip key={idx} label={product} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < recommendations.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  /**
   * Render sensor correlation section
   */
  const renderSensorCorrelation = () => {
    const sensorData = analysis.sensorCorrelation || {};
    const hasSensorData = Object.keys(sensorData).length > 0;

    return (
      <Accordion
        expanded={expandedPanel === 'sensors'}
        onChange={handleAccordionChange('sensors')}
        elevation={2}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <ThermostatAuto color="info" />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Données capteurs corrélées
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!hasSensorData ? (
            <Alert severity="info">
              Aucune donnée de capteur disponible pour cette période.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {sensorData.temperature && (
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Température
                      </Typography>
                      <Typography variant="h6">
                        {sensorData.temperature}°C
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {sensorData.humidity && (
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Humidité
                      </Typography>
                      <Typography variant="h6">
                        {sensorData.humidity}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {sensorData.soilMoisture && (
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Humidité du sol
                      </Typography>
                      <Typography variant="h6">
                        {sensorData.soilMoisture}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {sensorData.light && (
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Luminosité
                      </Typography>
                      <Typography variant="h6">
                        {sensorData.light} lux
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {sensorData.timestamp && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Mesures au moment de la photo: {new Date(sensorData.timestamp).toLocaleString('fr-FR')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      {/* Header with actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Résultats de l'analyse IA
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onExportPDF && (
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={onExportPDF}
              size="small"
            >
              Export PDF
            </Button>
          )}
          {onShare && (
            <IconButton onClick={onShare} size="small" color="primary">
              <Share />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Analysis timestamp */}
      {analysis.analyzedAt && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Analyse effectuée le {new Date(analysis.analyzedAt).toLocaleString('fr-FR')}
        </Alert>
      )}

      {/* Image preview avec URL signée */}
      {imageUrl && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Image analysée
          </Typography>
          <img
            src={imageUrl}
            alt="Image analysée"
            style={{
              width: '100%',
              maxHeight: 360,
              objectFit: 'contain',
              borderRadius: 8,
              border: '1px solid rgba(0,0,0,0.1)'
            }}
            onError={(e) => {
              console.error('❌ Erreur chargement image:', imageUrl);
              e.target.style.display = 'none';
            }}
            onLoad={() => {
              console.log('✅ Image chargée avec succès:', imageUrl);
            }}
          />
        </Box>
      )}

      {/* Analysis sections */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {renderHealthStatus()}
        {renderDiseases()}
        {renderPests()}
        {renderGrowthAnomalies()}
        {renderFruitMeasurements()}
        {renderRecommendations()}
        {renderSensorCorrelation()}
      </Box>
    </Box>
  );
};

export default ImageAnalysisResults;
